import { useEffect, type RefObject } from 'react';
import { consumeFlipSource } from '@/lib/flipTransition';

/**
 * Animează imaginea principală din galerie pornind din poziția cardului pe care
 * utilizatorul tocmai a dat click (FLIP). Clona e detașată de React, deci
 * animația supraviețuiește unei re-montări a galeriei.
 */
export function useGsapFlipImage(ref: RefObject<HTMLImageElement | null>, src?: string | null) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = ref.current;
    if (!target || !src) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const source = consumeFlipSource(src);
    if (!source) return;

    const to = target.getBoundingClientRect();
    if (to.width < 40 || to.height < 40) return;
    const targetRadius = getComputedStyle(target).borderRadius || '0px';

    // Clonă „fixed" peste pagină, pornind din poziția cardului.
    const clone = document.createElement('img');
    clone.src = source.src;
    clone.alt = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.dataset.flipClone = 'true';
    Object.assign(clone.style, {
      position: 'fixed',
      top: `${source.rect.top}px`,
      left: `${source.rect.left}px`,
      width: `${source.rect.width}px`,
      height: `${source.rect.height}px`,
      objectFit: 'cover',
      borderRadius: source.radius,
      zIndex: '60',
      pointerEvents: 'none',
      willChange: 'top, left, width, height',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(clone);

    target.style.opacity = '0';
    const restore = () => {
      target.style.opacity = '1';
      const live = ref.current;
      if (live) live.style.opacity = '1';
    };
    const safety = window.setTimeout(() => {
      clone.remove();
      restore();
    }, 2000);

    void (async () => {
      const { gsap } = await import('@/lib/gsap');
      gsap
        .timeline({
          onComplete: () => {
            window.clearTimeout(safety);
            restore();
            clone.remove();
          },
        })
        .to(clone, {
          top: to.top,
          left: to.left,
          width: to.width,
          height: to.height,
          borderRadius: targetRadius,
          duration: 0.55,
          ease: 'power3.inOut',
        })
        .to(clone, { opacity: 0, duration: 0.18, ease: 'power1.out' }, '-=0.12');
    })();

    return () => {
      window.clearTimeout(safety);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
}
